import { Component, Input, Output, ViewEncapsulation, EventEmitter, ChangeDetectionStrategy } from "@angular/core";
import { isPlatformServer } from "@angular/common";
import { BarVertical2DComponent, DataItem, calculateViewDimensions, ChartCommonModule, AxesModule, BarChartModule } from "@swimlane/ngx-charts";
import { CurrencyPipe } from "@angular/common"
import { KilowantColorHelper } from "./KilowantColorHelper"
import { select } from "d3-selection";
import { scaleBand } from 'd3-scale';
import { environment } from "../../../environments/environment";
import { KilowantSeriesVerticalHomeAcceptOfferComponent } from "./kilowant-charts-series-vertical-home-accept-offer.component";

@Component({
    selector: "kilowant-charts-home-accept-offer",
    templateUrl: "../../../html/chart/kilowant-charts-home-accept-offer.html",
    styleUrls: ["../../../css/base-chart.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ChartCommonModule, AxesModule, BarChartModule, KilowantSeriesVerticalHomeAcceptOfferComponent]
})

export class KilowantBarHomeAcceptOfferChartComponent extends BarVertical2DComponent {

    @Output() shift = new EventEmitter<number>();
    @Output() zoom = new EventEmitter<number>();
    @Output("gotoNext") gotoNextEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output("gotoCurrent") gotoCurrentEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Output("valLink") valLinkEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output("redValLink") redValLinkEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Input() val: number[] = [];
    @Input() valLabel: string = "";
    @Input() valColor: string = "#58504eEE";
    @Input() showNormalChatout: boolean = false;
    @Input() leftShift: number[] = [];
    @Input() rightShift: number[] = [];
    @Input() chatoutLeft: boolean = false;
    @Input() chatoutRight: boolean = false;

    @Input() shadow: boolean = false;

    @Output("datasetChanged") datasetChangedEmitter: EventEmitter<number> = new EventEmitter<number>();
    @Input() datasetSelector: boolean = false;
    @Input() selectedDatasets: number[] = [];

    @Input() myContractRate: number = 0;
    @Input() myContractColor = "#58504eff";
    @Input() myContractInFuture: boolean = false;

    @Input() nextRate: number = 0;
    @Input() nextRateColor: string = "";
    @Input() nextRateLabel: string = "";
    @Input() showNextRateCallout: boolean = false;
    @Input() nextCalloutLeft: boolean = false;
    @Input() nextCalloutRight: boolean = false;

    @Input() utilityWarningDate: string | null = null;
    @Input() communityWarningDate: string | null = null;

    @Input() utilityAverageRate: number = 0;
    @Input() communityAverageRate: number = 0;
    @Input() useCommunityAsText: boolean = false;

    @Input() averageLineValue: number = 0;
    @Input() averageLineColor: string | null = null;
    @Input() hideRenewable: boolean = false;

    @Input() prefix: string = "";

    BIG_MAX: number = 0.5;

    yScaleMin: number | null = null;

    countColumns: number;

    colorHelper: KilowantColorHelper;
    legendData: string[] = [];

    currentShift: number = 0;
    currentZoom: number = 1;
    extendedHeight: number = 0;

    utilityName: string = 'RI Energy';
    utilityNameForWarning = 'RI Energy Last Resort Service';
    communityName: string = 'NextEra Energy';
    communityNameForWarning = 'NextEra Energy';

    isSSR = false;
    initiated: boolean = false;

    RIColor: string = "#" + environment.RI_UTILITY_COLOR;
    RIEstimatedColor: string = "#" + environment.RI_UTILITY_FORECASTED_COLOR;
    CommunityColor: string = "#" + environment.RI_COMMUNITY_COLOR;
    CommunityEstimatedColor: string = "#" + environment.RI_COMMUNITY_FORECASTED_COLOR;

    KilowantColor: string = "#" + environment.KILOWANT_COLOR;
    showKilowantLine: boolean = false;

    asterikVals: string[] = [];

    ngOnInit() {
        this.countColumns = this.results.length;
        if (isPlatformServer(this.platformId)) {
            this.isSSR = true;
        }
        this.animations = false;
        this.colorHelper = new KilowantColorHelper(this.colors, this.scheme, this.schemeType, [this.valLabel, "Present Rate", this.nextRateLabel, this.utilityName, this.utilityName + " Last Resort Service", this.utilityName + " (Projected)", this.communityName, this.communityName + " (Projected)", "Community", "Kilowant"], [this.valColor, this.myContractColor, this.nextRateColor, this.RIColor, this.RIColor, this.RIEstimatedColor, this.CommunityColor, this.CommunityEstimatedColor, this.CommunityColor, this.KilowantColor]);
    }

    refreshLegend(): void {
        let asterisk = "*";
        this.asterikVals = [];
        this.legendData = [];
        if (this.results.length > 0) {
            for (let x = 0; x < this.results.length; x++) {
                for (let i = 0; i < this.results[x].series.length; i++) {
                    let testVal = this.results[x].series[i].name.replace(/\s*\(Projected\)\s*/, "");
                    if (testVal == this.utilityName) {
                        if (!this.asterikVals.includes(testVal)) {
                            this.asterikVals.push(testVal);
                        }
                        let asteriskNum = this.asterikVals.indexOf(testVal) + 1;
                        if (this.utilityWarningDate != null && this.utilityWarningDate != "" && this.containsForecasted(this.utilityName)) {
                            testVal = testVal + this.repeater(asterisk, asteriskNum);
                        }
                        if (this.utilityAverageRate > 0) {
                            testVal = testVal + " " + this.yAxisFormat(this.utilityAverageRate) + "/kWh";
                        }
                    }
                    if (testVal == this.communityName) {
                        if (this.communityWarningDate != null && this.communityWarningDate != "" && this.containsForecasted(this.communityName)) {
                            if (!this.asterikVals.includes(testVal)) {
                                this.asterikVals.push(testVal);
                            }
                            let asteriskNum = this.asterikVals.indexOf(testVal) + 1;
                            testVal = testVal + this.repeater(asterisk, asteriskNum);
                        }
                        if (this.communityAverageRate > 0) {
                            testVal = testVal + " " + this.yAxisFormat(this.communityAverageRate) + "/kWh";
                        }
                        if (this.useCommunityAsText) {
                            testVal = "Community";
                        }
                    }
                    if (!this.legendData.includes(testVal) && testVal != null && testVal.length > 0) {
                        this.legendData.push(testVal);
                    }
                }
            }
        }
        if (this.val.length > 0) {
            if (this.val.length == 1) {
                if (this.val[0] > 0) {
                    this.legendData.push(this.valLabel + " " + this.yAxisFormat(this.val[0]) + "/kWh");
                }
            } else {
                let sum = 0;
                for (var i = 0; i < this.val.length; i++) {
                    sum += this.val[i];
                }
                if (sum > 0) {
                    this.legendData.push(this.valLabel + " " + this.yAxisFormat(sum / this.val.length) + "/kWh");
                }
            }
        }
        if (this.nextRate > 0) {
            if (!this.legendData.includes(this.nextRateLabel)) {
                this.legendData.push(this.nextRateLabel + " " + this.yAxisFormat(this.nextRate) + "/kWh");
            }
        }
        if (this.myContractRate > 0) {
            this.legendData.push("Present Rate" + " " + this.yAxisFormat(this.myContractRate) + "/kWh");
        }
        if (this.getActivePeaksPolylinePoints()) {
            this.showKilowantLine = true;
        }
    }

    update(): void {
        this.yScaleMax = Math.max(Math.max(...this.val), this.myContractRate, this.nextRate) * 1.05;

        this.yScaleMin = null;
        if (this.val.length > 0) {
            this.yScaleMin = Math.min(...this.val);
        }
        if (this.myContractRate > 0 && (this.yScaleMin == null || this.myContractRate < this.yScaleMin)) {
            this.yScaleMin = this.myContractRate;
        }
        if (this.nextRate > 0 && (this.yScaleMin == null || this.nextRate < this.yScaleMin)) {
            this.yScaleMin = this.nextRate;
        }

        this.extendedHeight = this.height;

        this.valueDomain = this.getValueDomain();
        if (this.yScaleMax == 0 && this.valueDomain[1] == 0) {
            this.yScaleMax = this.BIG_MAX;
        }
        this.yAxisTicks = [];
        if (this.yScaleMin == null) {
            this.yScaleMin = 0;
        }
        var step = (Math.max(this.valueDomain[0], this.valueDomain[1]) - this.valueDomain[0]) / 9;
        for (var i = 0; i <= 9; i++) {
            this.yAxisTicks.push(this.valueDomain[0] + i * step);
        }
        super.update();
        this.dims = calculateViewDimensions({
            width: this.width,
            height: this.height,
            margins: this.margin,
            showXAxis: this.xAxis,
            showYAxis: this.yAxis,
            xAxisHeight: this.xAxisHeight,
            yAxisWidth: this.yAxisWidth,
            showXLabel: this.showXAxisLabel,
            showYLabel: this.showYAxisLabel,
            showLegend: this.legend,
            legendType: this.schemeType,
            legendPosition: this.legendPosition
        });

        if (this.dims.xOffset != null && this.dataLabelMaxHeight.negative != null && this.margin[0] != null && (this.margin[0] + this.dataLabelMaxHeight.negative) > 0) {
            this.transform = `translate(${this.dims.xOffset - 2} , ${this.margin[0] + this.dataLabelMaxHeight.negative})`;
        }

        if (this.myContractRate > 0) {
            this.drawMyKilowantRate(this.myContractRate, true);
        }
        if (this.nextRate > 0) {
            this.drawMyKilowantRate(this.nextRate, false);
        }

        for (let i = 0; i < 5; i++) {
            this.clearPriceLine("price-" + (i + 1));
        }
        if (this.val.length > 0) {
            for (let i = 0; i < this.val.length; i++) {
                this.drawPriceLine(this.valColor, "price-" + (i + 1), this.getYValPerPrice(this.val[i]), this.leftShift[i], this.rightShift[i], this.showNormalChatout, this.chatoutLeft, this.chatoutRight);
            }
        }

        this.clearPriceLine("average");
        if (this.averageLineValue != null && this.averageLineValue > 0) {
            this.drawPriceLine(this.averageLineColor!, "average", this.getYValPerPrice(this.averageLineValue), 0, 0, false, false, false);
        }

        this.refreshLegend();
    }

    getValueDomain(): [number, number] {
        const domain: number[] = [];
        for (const group of this.results) {
            if (group != null) {
                for (const d of group.series) {
                    if (!domain.includes(d.value) && d.value > 0) {
                        domain.push(d.value);
                    }
                }
            }
        }

        const min = this.yScaleMin ? Math.min(this.yScaleMin, ...domain) * 0.8 : Math.min(...domain) * 0.8;
        const max = this.yScaleMax ? Math.max(this.yScaleMax, ...domain) : Math.max(0, ...domain);
        return [min, max];
    }

    getSeries(group: any): DataItem[] {
        var series: DataItem[] = [];
        for (let i = 0; i < group.series.length; i++) {
            var im = Object.assign(new Object(), group.series[i]);
            if (group.series[i].isForecasted) {
                im.name = im.name + " (Projected)";
                im.label = im.label + " (Projected)";
            }
            series.push(im);
        }
        return series;
    }

    /** True when the given serie (or bar's underlying data) is marked inactive. */
    private isInactive(item: any): boolean {
        if (item == null) { return false; }
        if (item.isInactive === true) { return true; }
        if (item.data != null && item.data.isInactive === true) { return true; }
        return false;
    }

    /**
     * Returns the `points` attribute for the active-trend polyline, or `null`
     * when nothing should be drawn.
     *
     * Rendered only when at least one serie in the current page is inactive —
     * otherwise the whole chart is active and the eye doesn't need a guide.
     *
     * The line:
     *   • starts at the left border of the bar area   (x = 0),
     *   • wraps the top of every active bar (two points per bar, 2px overhang
     *     on each side, each raised above the bar's top),
     *   • connects consecutive peaks diagonally through the gaps between bars,
     *   • ends at the right border of the bar area    (x = dims.width).
     *
     * That way it visually reads as "this is how the effective price evolves
     * across the whole timeline", and it never crosses an active bar.
     */
    getActivePeaksPolylinePoints(): string | null {
        if (this.results == null || this.results.length === 0) { return null; }
        if (this.groupScale == null || this.innerScale == null) { return null; }
        if (this.dims == null || this.dims.width == null) { return null; }

        let hasInactive = false;
        const middlePoints: string[] = [];
        const innerScale = this.innerScale as any;
        const barWidth = typeof innerScale.bandwidth === "function" ? innerScale.bandwidth() : 0;
        // Horizontal overhang on each side of the peak segment so the line does
        // not hug the bar edges.
        const PEAK_PADDING = 2;

        let firstY: number | null = null;
        let lastY: number | null = null;

        for (const group of this.results) {
            if (group == null || group.series == null) { continue; }

            const groupX = this.groupScale(group.name);
            if (groupX == null || !isFinite(groupX)) { continue; }

            for (const s of group.series) {
                if (this.isInactive(s)) {
                    hasInactive = true;
                    continue;
                }

                const seriesX = innerScale(s.name);
                if (seriesX == null || !isFinite(seriesX)) { continue; }

                const barLeft = groupX + seriesX;
                const barRight = barLeft + barWidth;
                const y = this.getYValPerPrice(s.value);

                if (firstY == null) { firstY = y; }
                lastY = y;

                middlePoints.push(`${barLeft - PEAK_PADDING},${y}`);
                middlePoints.push(`${barRight + PEAK_PADDING},${y}`);
            }
        }

        if (!hasInactive || middlePoints.length < 2 || firstY == null || lastY == null) { return null; }

        const points: string[] = [];
        // Extend the trend line to the left border of the chart area.
        points.push(`0,${firstY}`);
        for (const p of middlePoints) { points.push(p); }
        // Extend the trend line to the right border of the chart area.
        points.push(`${this.dims.width},${lastY}`);

        return points.join(" ");
    }

    containsForecasted(suplierName: string): boolean {
        if (this.results == null || this.results.length == 0) {
            return false;
        }
        for (const group of this.results) {
            if (group != null) {
                for (const d of group.series) {
                    if (d.name == suplierName && d.isForecasted) {
                        return true;
                    }
                }
            }
        }
        return false;
    }

    getInnerScale(): any {
        const width = this.groupScale.bandwidth();
        const spacing = this.innerDomain.length / (width / this.barPadding + 1);
        let innerDomain = this.innerDomain;
        let initModified = function () {
            let d3 = scaleBand().rangeRound([0, width]).paddingInner(spacing).domain(innerDomain);
            function d3Based(label: string) {
                label = label.replace(/\s*\(Projected\)\s*/, "");
                return d3(label);
            }
            d3Based.bandwidth = function () {
                return d3.bandwidth();
            }
            return d3Based;
        }
        return initModified();
    }

    getYValPerPrice(val: number): number {
        if (val > 0 && this.valueDomain[1] > 0) {
            let y = Math.round(this.dims.height * ((this.valueDomain[1] - val) / (this.valueDomain[1] - this.valueDomain[0])));
            return y;
        }
        return 0;
    }

    drawPriceLine(color: string, id: string, y: number, leftShift: number, rightShift: number, showChatOut: boolean, chatoutPointLeft: boolean, chatoutPointRight: boolean) {
        const w = this.dims.width;
        var x1 = 0;
        var x2 = w;
        if (leftShift > 0) {
            x1 = x1 + leftShift * this.getGroupScale().step();
        }
        if (rightShift > 0) {
            x2 = x2 - rightShift * this.getGroupScale().step();
        }
        select("#" + id + "-line" + this.prefix)
            .attr("x1", x1)
            .attr("x2", x2)
            .attr("y1", y)
            .attr("y2", y)
            .attr("stroke", color);

        if (showChatOut) {
            this.drawChatOut(x1 + (x2 - x1) / 2, y, chatoutPointLeft, chatoutPointRight, color);
        }
    }

    clearPriceLine(id: string) {
        select("#" + id + "-line" + this.prefix)
            .attr("x1", null)
            .attr("x2", null)
            .attr("y1", null)
            .attr("y2", null)
            .attr("stroke", null);
        select("#" + id + "-text" + this.prefix)
            .text("").attr("x", null).attr("y", null)
            .attr("fill", null);
    }

    drawChatOut(x: number, y: number, pointLeft: boolean, pointRight: boolean, color: string): void {
        let point1 = 30;
        let point2 = 110;
        if (pointLeft) {
            point1 = -60;
            point2 = 20;
        } else if (pointRight) {
            point1 = 180;
            point2 = 260;
        }
        select("#chatout" + this.prefix).attr("points", this.p(x, y) + this.p(point1, -60) + this.p(point2, -60))
            .attr("fill", (color.length == 7) ? color + "ff" : color.substring(0, color.length - 2) + "ff")
            .attr("stroke", "none");
    }

    drawMyKilowantRate(val: number, isMyKilowant: boolean): void {
        let y = this.getYValPerPrice(val);
        let x = this.dims.width;
        let prefix = this.prefix;
        if (isMyKilowant) {
            if (!this.myContractInFuture) {
                select("#myContractArrow" + this.prefix).attr("points", this.p(0, y) + this.p(10, y - 10) + this.p(10, y + 10));
                select("#myContractText" + this.prefix)
                    .text((new CurrencyPipe("en-US")).transform(val, "USD", "symbol", "1.3-3") + "/kWh")
                    .attr("x", 12)
                    .attr("y", y + 5);
            } else {
                var textWidth = 0;
                select("#myContractText" + this.prefix)
                    .text((new CurrencyPipe("en-US")).transform(val, "USD", "symbol", "1.3-3") + "/kWh")
                    .attr("x", function (d) {
                        let xNum = 0;
                        if (this != null) {
                            textWidth = (<SVGTextElement>this).getComputedTextLength();
                            xNum = x - textWidth - 15;
                        } else {
                            xNum = 0;
                        }
                        return xNum;
                    })
                    .attr("y", y + 5);
                select("#myContractArrow" + this.prefix).attr("points", this.p(x - 10, y - 10) + this.p(x, y) + this.p(x - 10, y + 10));
            }
        } else {
            select("#nextText" + this.prefix)
                .text((new CurrencyPipe("en-US")).transform(val, "USD", "symbol", "1.3-3") + "/kWh")
                .attr("x", function (d) {
                    let xNum = 0;
                    if (this != null) {
                        textWidth = (<SVGTextElement>this).getComputedTextLength();
                    }
                    xNum = x - textWidth - 15;
                    return xNum;
                })
                .attr("fill", this.nextRateColor)
                .attr("y", y + 5);
            select("#nextArrow" + this.prefix).attr("points", this.p(x - 10, y - 10) + this.p(x, y) + this.p(x - 10, y + 10))
                .attr("fill", this.nextRateColor);

            if (this.showNextRateCallout) {
                this.drawChatOut(x - 40, y - 10, this.nextCalloutLeft, this.nextCalloutRight, this.nextRateColor);
            }
        }
    }

    yAxisFormat(val: number): string | null {
        return (new CurrencyPipe("en-US")).transform(val, "USD", "symbol", "1.3-3");
    }

    goNextSlide(): void {
        this.gotoNextEmitter.next(true);
    }

    goNowSlide(): void {
        this.gotoCurrentEmitter.next(true);
    }

    goToVal(): void {
        this.valLinkEmitter.next(true);

    }

    goToRedVal(): void {
        this.redValLinkEmitter.next(true);
    }

    repeater(chr: string, num: number) {
        let outStr = "";
        for (let i = 0; i < num; i++) {
            outStr = outStr + chr;
        }
        return outStr;
    }

    private p(x: number, y: number): string {
        return x + "," + y + " ";
    }
}